/**
 * Notifications API Service
 *
 * HTTP client for notification campaign endpoints.
 * Uses mock segments data since the segments list endpoint is not yet available.
 */

import { apiClient as api } from "../../services/api/client";
import { segmentsApi } from "../segments/api";
import type { NotificationCampaign, SegmentSummary } from "./types";

/** Standard API response type */
type ApiResponse<T> = { success: boolean; data: T; error?: string };

/**
 * Notifications API
 */
export const notificationsApi = {
  /**
   * Get saved segments from the segments store (same data as SegmentsPage).
   * NOTE: Reemplazar con endpoint dedicado de notificaciones cuando esté disponible.
   */
  async getSegments(brandId: string): Promise<ApiResponse<SegmentSummary[]>> {
    const res = await segmentsApi.list(brandId, "saved");
    if (!res.success) return { success: false, data: [], error: res.error };
    const summaries: SegmentSummary[] = res.data.map((s) => ({
      id: s.id,
      name: s.name || "Segmento sin nombre",
      lastKnownCount: s.lastKnownCount || 0,
      status: s.status,
    }));
    return { success: true, data: summaries };
  },

  /**
   * Create a notification campaign
   */
  async create(params: {
    brandId: string;
    segmentId: string;
    title: string;
    body: string;
    link?: string;
    imageUrl?: string;
  }): Promise<ApiResponse<NotificationCampaign>> {
    const response = await api.post("/notifications", params);
    return response.data;
  },

  /**
   * Send a campaign immediately
   */
  async send(id: string): Promise<ApiResponse<NotificationCampaign>> {
    const response = await api.post(`/notifications/${id}/send`);
    return response.data;
  },

  /**
   * Schedule a campaign
   */
  async schedule(
    id: string,
    scheduledAt: string,
  ): Promise<ApiResponse<NotificationCampaign>> {
    const response = await api.post(`/notifications/${id}/schedule`, {
      scheduledAt,
    });
    return response.data;
  },

  /**
   * List campaigns for a brand
   */
  async list(brandId: string): Promise<ApiResponse<NotificationCampaign[]>> {
    try {
      const response = await api.get(`/notifications?brandId=${brandId}`);
      return response.data;
    } catch (err: any) {
      return {
        success: false,
        data: [],
        error:
          err?.response?.data?.error ||
          err.message ||
          "Error al cargar campañas",
      };
    }
  },

  /**
   * Aggregate analytics across campaigns (last N days).
   */
  async analytics(
    brandId: string,
    days: number = 30,
  ): Promise<
    ApiResponse<{
      totals: {
        sent: number;
        delivered: number;
        opened: number;
        clicked: number;
        failed: number;
        campaignsCount: number;
      };
      rates: { delivery: number; open: number; click: number };
      perDay: Array<{
        day: string;
        entregado: number;
        abierto: number;
        clickeado: number;
      }>;
      recentCampaigns: NotificationCampaign[];
    }>
  > {
    try {
      const response = await api.get(`/notifications/analytics`, {
        params: { brandId, days },
      });
      return response.data;
    } catch (err: any) {
      return {
        success: false,
        data: {} as any,
        error:
          err?.response?.data?.error ||
          err.message ||
          "Error al cargar métricas",
      };
    }
  },
};

export default notificationsApi;
